import {
  Button,
  Calendar,
  DateInput,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@design-system';

import { CalendarIcon } from 'lucide-react';

import { useState } from 'react';
import { PrefillPlanRequiredLabel } from './prefill-plan-required-label';

const EARLIEST_BIRTH_DATE = new Date(1900, 0, 1);

interface PrefillDateFieldProps {
  id: string;
  label: string;
  /** Marks an answer that contributes to the quick plan. */
  planRequired?: boolean;
  /** Uses the standard required-field treatment instead of the quick-plan tooltip. */
  required?: boolean;
  value?: Date;
  inputValue?: string;
  error?: string;
  onChange: (date: Date | undefined) => void;
  onInputValueChange?: (value: string) => void;
  onInputValidityChange?: (isValid: boolean) => void;
}

export function PrefillDateField({
  id,
  label,
  value,
  inputValue,
  error,
  onChange,
  onInputValueChange,
  onInputValidityChange,
  planRequired,
  required,
}: PrefillDateFieldProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const today = new Date();

  return (
    <div className="flex flex-col gap-2">
      {planRequired ? (
        <PrefillPlanRequiredLabel htmlFor={id}>{label}</PrefillPlanRequiredLabel>
      ) : (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      <div className="flex items-center gap-2">
        <DateInput
          id={id}
          className="flex-1"
          aria-invalid={!!error}
          value={value}
          inputValue={inputValue}
          onChange={onChange}
          onInputValueChange={onInputValueChange}
          onInputValidityChange={onInputValidityChange}
        />
        <Popover
          open={isCalendarOpen}
          onOpenChange={setIsCalendarOpen}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={`Choose a date for: ${label}`}
            >
              <CalendarIcon className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="end"
          >
            <Calendar
              mode="single"
              // Birth dates are decades back, so month/year dropdowns beat paging.
              captionLayout="dropdown"
              startMonth={EARLIEST_BIRTH_DATE}
              endMonth={today}
              defaultMonth={value ?? today}
              selected={value}
              disabled={{ before: EARLIEST_BIRTH_DATE, after: today }}
              onSelect={date => {
                onInputValidityChange?.(true);
                onChange(date);
                setIsCalendarOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      {error && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {error}
        </p>
      )}
    </div>
  );
}
